import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import type * as React from 'react';
import type { BatchReserveResponse } from '@ooh/contracts';
import { Button } from '@ooh/ui/components/button';
import { DisabledHintTooltip } from '@ooh/ui/components/disabled-hint-tooltip';
import { formatDisplayDate, formatDisplayDateRange } from '@ooh/ui/lib/date-format';
import { RESULT_PANEL_WRAPPER_CLASS } from '../../reservations/components/outcome-result-buckets';
import { describeBatchOutcomeFailure } from '../../reservations/lib/error-toast';

interface UnifiedSelectActionsProps {
  canSubmitReserve: boolean;
  canRelease: boolean;
  onOption: () => void;
  onReserve: () => void;
  onRelease: () => void;
}

export function UnifiedSelectActions({
  canSubmitReserve,
  canRelease,
  onOption,
  onReserve,
  onRelease,
}: UnifiedSelectActionsProps) {
  const { t } = useTranslation('inventory');
  return (
    <div className="grid grid-cols-2 gap-2 pt-1">
      <Button
        type="button"
        size="xs"
        variant="outline"
        fullWidth
        disabled={!canSubmitReserve}
        data-testid="unified-select-option-btn"
        onClick={onOption}
      >
        {t('availability.select.optionBtn')}
      </Button>
      <Button
        type="button"
        size="xs"
        fullWidth
        disabled={!canSubmitReserve}
        data-testid="unified-select-reserve-btn"
        onClick={onReserve}
      >
        {t('availability.select.reserveBtn')}
      </Button>
      <Button
        type="button"
        size="xs"
        variant="destructive"
        fullWidth
        className="col-span-2"
        disabled={!canRelease}
        data-testid="unified-select-release-btn"
        onClick={onRelease}
      >
        {t('availability.select.releaseBtn')}
      </Button>
    </div>
  );
}

/**
 * The own-held "Dodaj wpis dla innego klienta" slot. The caller-supplied panel wins; while its
 * query loads nothing renders (the placeholder would claim "not applicable"); a selection that
 * holds an own cell but has no panel, such as a multi-cell one, shows a disabled placeholder.
 * A selection without any own-held cell renders nothing. A function rather than a component so
 * the popover can tell "no slot" apart and skip the wrapper element.
 */
export function resolveQueueJoinNode(
  ownHeldCount: number,
  queueJoinSection: React.ReactNode | undefined,
  queueJoinLoading: boolean,
  labels: { disabledHint: string; addAnotherClientLabel: string },
): React.ReactNode {
  if (ownHeldCount === 0) return null;
  if (queueJoinSection) return queueJoinSection;
  if (queueJoinLoading) return null;
  return (
    <DisabledHintTooltip disabled hint={labels.disabledHint}>
      <Button
        type="button"
        size="xs"
        variant="outline"
        fullWidth
        disabled
        data-testid="unified-select-queue-join-disabled-btn"
      >
        <Plus className="h-3.5 w-3.5" />
        {labels.addAnotherClientLabel}
      </Button>
    </DisabledHintTooltip>
  );
}

function ResultCount({ testId, count, label }: { testId: string; count: number; label: string }) {
  return (
    <div data-testid={testId}>
      <p className="font-semibold">{count}</p>
      <p className="text-muted-foreground">{label}</p>
    </div>
  );
}

/**
 * The last Rezerwacja outcome as reserved / queued / failed groups, the same vocabulary the lock
 * dialog's result uses. Queued periods are a normal outcome, so only the failed group is styled
 * destructive.
 */
export function ReserveResultPanel({
  result,
  onDismiss,
}: {
  result: BatchReserveResponse;
  onDismiss: () => void;
}) {
  const { t } = useTranslation('inventory');
  const { t: tReservations } = useTranslation('reservations');
  const { created, queued, conflicted } = result;
  return (
    <div className={RESULT_PANEL_WRAPPER_CLASS} data-testid="unified-select-reserve-result">
      <div className="grid grid-cols-3 gap-1 text-center text-xs">
        <ResultCount
          testId="unified-select-reserve-result-reserved-count"
          count={created.length}
          label={t('availability.select.reserveResult.reservedLabel')}
        />
        <ResultCount
          testId="unified-select-reserve-result-queued-count"
          count={queued.length}
          label={t('availability.select.reserveResult.queuedLabel')}
        />
        <ResultCount
          testId="unified-select-reserve-result-failed-count"
          count={conflicted.length}
          label={t('availability.select.reserveResult.failedLabel')}
        />
      </div>
      {created.length > 0 ? (
        <ul className="space-y-0.5 text-xs text-muted-foreground">
          {created.map((entry) => (
            <li key={`created-${entry.mediaUnitId}-${entry.dateFrom.toISOString()}`}>
              {formatDisplayDateRange(entry.dateFrom, entry.dateTo)}
              {entry.ttlExpiresAt
                ? ` — ${t('availability.select.reserveResult.expiresOn', { date: formatDisplayDate(entry.ttlExpiresAt) })}`
                : ''}
            </li>
          ))}
        </ul>
      ) : null}
      {queued.length > 0 ? (
        <ul className="space-y-0.5 text-xs text-muted-foreground">
          {queued.map((entry) => (
            <li key={`queued-${entry.mediaUnitId}-${entry.dateFrom.toISOString()}`}>
              {formatDisplayDateRange(entry.dateFrom, entry.dateTo)} —{' '}
              {tReservations('waitlist.inQueueLabel', { position: entry.position })}
            </li>
          ))}
        </ul>
      ) : null}
      {conflicted.length > 0 ? (
        <ul className="space-y-0.5 text-xs text-destructive">
          {conflicted.map((entry) => (
            <li key={`failed-${entry.mediaUnitId}-${entry.dateFrom.toISOString()}`}>
              {formatDisplayDateRange(entry.dateFrom, entry.dateTo)} —{' '}
              {describeBatchOutcomeFailure(
                entry,
                tReservations as (key: string, options?: Record<string, unknown>) => string,
              )}
            </li>
          ))}
        </ul>
      ) : null}
      <Button
        type="button"
        variant="link"
        size="xs"
        className="h-auto p-0 text-xs"
        onClick={onDismiss}
        data-testid="unified-select-reserve-result-dismiss"
      >
        {t('availability.select.reserveResult.dismissBtn')}
      </Button>
    </div>
  );
}
